"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";

type SleepMode = "wake" | "bed";

type Strings = { [K in keyof typeof SLEEP_CALCULATOR_STRINGS]: string };

const SLEEP_MODES: { value: SleepMode; label: keyof Strings }[] = [
  { value: "wake", label: "modeWake" },
  { value: "bed", label: "modeBed" },
];

const MINUTES_PER_DAY = 24 * 60;
const CYCLE_MINUTES = 90;
const FALL_ASLEEP_MINUTES = 15;

type SleepLabelKey = "sleep9h" | "sleep7h30" | "sleep6h";

const CYCLE_OPTIONS: { cycles: number; sleepLabel: SleepLabelKey }[] = [
  { cycles: 6, sleepLabel: "sleep9h" },
  { cycles: 5, sleepLabel: "sleep7h30" },
  { cycles: 4, sleepLabel: "sleep6h" },
];

const TIME_INPUT_REGEX = /^(\d{1,2}):(\d{2})$/;

function parseTimeToMinutes(value: string): number | null {
  const match = TIME_INPUT_REGEX.exec(value);
  if (!match) {
    return null;
  }
  const hours = Number.parseInt(match[1], 10);
  const minutes = Number.parseInt(match[2], 10);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
}

function wrapMinutes(totalMinutes: number): number {
  return ((totalMinutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
}

function formatMinutes(totalMinutes: number, strings: Strings): string {
  const wrapped = wrapMinutes(totalMinutes);
  const hours24 = Math.floor(wrapped / 60);
  const minutes = wrapped % 60;
  const period = hours24 >= 12 ? strings.periodPm : strings.periodAm;
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${period}`;
}

function bedtimesForWake(
  wakeMinutes: number
): { time: number; sleepLabel: SleepLabelKey }[] {
  return CYCLE_OPTIONS.map((option) => ({
    time: wrapMinutes(
      wakeMinutes - FALL_ASLEEP_MINUTES - option.cycles * CYCLE_MINUTES
    ),
    sleepLabel: option.sleepLabel,
  }));
}

function wakeTimesForBed(
  bedMinutes: number
): { time: number; sleepLabel: SleepLabelKey }[] {
  return CYCLE_OPTIONS.slice()
    .reverse()
    .map((option) => ({
      time: wrapMinutes(
        bedMinutes + FALL_ASLEEP_MINUTES + option.cycles * CYCLE_MINUTES
      ),
      sleepLabel: option.sleepLabel,
    }));
}

function TimeCard({
  time,
  sleepLabel,
  strings,
}: {
  time: number;
  sleepLabel: SleepLabelKey;
  strings: Strings;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">
        {formatMinutes(time, strings)}
      </p>
      <p className="mt-1 text-muted-foreground text-sm">
        {strings[sleepLabel]}
      </p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const SLEEP_CALCULATOR_STRINGS = {
  mode: "Mode",
  wakeUpTime: "Wake-up time",
  suggestedBedtimes: "Suggested Bedtimes",
  eachBedtimeAllows15Minutes:
    "Each bedtime allows ~15 minutes to fall asleep, then 6, 5, or 4 complete 90-minute cycles before your alarm.",
  calculateWakeUpTimes: "Calculate Wake-Up Times",
  recalculateFromNow: "Recalculate From Now",
  ifYouFallAsleepBy: "If You Fall Asleep by",
  basedOnGoingToBed: "Based on going to bed at ",
  taking15MinutesToFall:
    ", taking ~15 minutes to fall asleep, then sleeping 4, 5, or 6 full cycles.",
  howTheMathWorks: "How the Math Works",
  bedtimeWakeTime15Min:
    "bedtime = wake time − 15 min buffer − (cycles × 90 min)",
  aFullSleepCycleAverages:
    "A full sleep cycle averages about 90 minutes, but that is only an average — individual cycles range from roughly 70 to 120 minutes and shift over the course of the night. Treat these times as a starting point, not a precise prescription. This tool is not medical advice; if you have ongoing sleep problems, talk to a doctor.",
  modeWake: "I want to wake up at...",
  modeBed: "If I go to bed now...",
  sleep9h: "9h of sleep",
  sleep7h30: "7.5h of sleep",
  sleep6h: "6h of sleep",
  periodAm: "AM",
  periodPm: "PM",
} as const;

export default function SleepCalculatorWidget() {
  const [mode, setMode] = useState<SleepMode>("wake");
  const [wakeTime, setWakeTime] = useState("07:00");
  const [nowMinutes, setNowMinutes] = useState<number | null>(null);

  const wakeMinutes = parseTimeToMinutes(wakeTime);
  const bedtimes = wakeMinutes === null ? [] : bedtimesForWake(wakeMinutes);
  const wakeTimes = nowMinutes === null ? [] : wakeTimesForBed(nowMinutes);

  function handleUseCurrentTime() {
    const now = new Date();
    setNowMinutes(now.getHours() * 60 + now.getMinutes());
  }

  const strings = useToolUi(SLEEP_CALCULATOR_STRINGS);

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.mode}</p>
        <div className="flex flex-wrap gap-3">
          {SLEEP_MODES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <input
                checked={mode === m.value}
                className="accent-primary"
                name="sleep-mode"
                onChange={() => setMode(m.value)}
                type="radio"
                value={m.value}
              />
              {strings[m.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Wake-up mode */}
      {mode === "wake" && (
        <section className="space-y-4">
          <div className="space-y-1">
            <label
              className="block font-medium text-foreground text-xs"
              htmlFor="wake-time"
            >
              {strings.wakeUpTime}
            </label>
            <input
              className="rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="wake-time"
              onChange={(e) => setWakeTime(e.target.value)}
              type="time"
              value={wakeTime}
            />
          </div>
          {bedtimes.length > 0 && (
            <>
              <h2 className="font-semibold text-foreground text-lg">
                {strings.suggestedBedtimes}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {bedtimes.map((b) => (
                  <TimeCard
                    key={b.sleepLabel}
                    sleepLabel={b.sleepLabel}
                    strings={strings}
                    time={b.time}
                  />
                ))}
              </div>
              <p className="text-muted-foreground text-sm">
                {strings.eachBedtimeAllows15Minutes}
              </p>
            </>
          )}
        </section>
      )}
      {/* Bed-now mode */}
      {mode === "bed" && (
        <section className="space-y-4">
          <button
            className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
            onClick={handleUseCurrentTime}
            type="button"
          >
            {nowMinutes === null
              ? strings.calculateWakeUpTimes
              : strings.recalculateFromNow}
          </button>
          {nowMinutes !== null && (
            <>
              <h2 className="font-semibold text-foreground text-lg">
                {strings.ifYouFallAsleepBy}{" "}
                {formatMinutes(nowMinutes + FALL_ASLEEP_MINUTES, strings)}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {wakeTimes.map((w) => (
                  <TimeCard
                    key={w.sleepLabel}
                    sleepLabel={w.sleepLabel}
                    strings={strings}
                    time={w.time}
                  />
                ))}
              </div>
              <p className="text-muted-foreground text-sm">
                {strings.basedOnGoingToBed}
                {formatMinutes(nowMinutes, strings)}
                {strings.taking15MinutesToFall}
              </p>
            </>
          )}
        </section>
      )}
      {/* How it works */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.howTheMathWorks}
        </h2>
        <p className="font-mono text-foreground text-sm">
          {strings.bedtimeWakeTime15Min}
        </p>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.aFullSleepCycleAverages}
        </p>
      </section>
    </>
  );
}
